import { FaFacebookF } from "react-icons/fa";
import { LuPhone, LuMail, LuMapPin, LuClock } from "react-icons/lu";
import { Link } from "react-router-dom";

import logo from "../assets/mega_electric_logo.png";
import { business, services, serviceAreas } from "../content/site";

/**
 * Site footer.
 *
 * Dark surface + `on-inverse` so the global focus ring flips to white.
 * Text stays at full `text-ink-inverse` rather than a dimmed white: hierarchy
 * comes from size and weight, which keeps every line at maximum contrast.
 */

/* Featured services only. There are no per-service pages, so every one of
   these points at the #services section rather than a dead URL. */
const footerServices = services.filter((service) => service.featured);

const companyLinks = [
  { href: "#why-us", label: "Why choose us" },
  { href: "#process", label: "How it works" },
  { href: "#service-areas", label: "Service areas" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact" },
];

/* Shared link styling. `min-h-tap` holds the 44px touch target in the lists. */
const LINK_CLASS =
  "relative inline-flex min-h-tap items-center gap-2 py-1 text-body text-ink-inverse " +
  "underline-offset-4 hover:underline focus-visible:underline " +
  "transition-colors duration-base ease-standard";

const ColumnHeading = ({ id, children }) => (
  <h2 id={id} className="text-label uppercase text-ink-inverse">
    {children}
  </h2>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="relative on-inverse bg-surface-inverse text-ink-inverse"
      aria-labelledby="footer-heading"
    >
      <h2 id="footer-heading" className="sr-only">
        Site footer
      </h2>

      {/*
        The bottom padding clears the sticky mobile call bar that is fixed to
        the bottom of the viewport below `lg`. Without it the last footer rows
        sit underneath the bar and cannot be read or tapped. Do not remove
        `pb-28` unless that bar is removed too.
      */}
      <div className="container-page pt-12 pb-28 lg:pt-14 lg:pb-8">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {/* Column 1 - identity */}
          <div className="min-w-0">
            <Link
              to="/"
              className="inline-flex items-center rounded-md"
              aria-label={`${business.legalName} home`}
            >
              <img
                src={logo}
                alt={business.legalName}
                width={946}
                height={532}
                className="h-14 w-auto"
              />
            </Link>

            <p className="mt-4 text-body font-semibold text-ink-inverse">
              {business.legalName}
            </p>
            <p className="mt-2 max-w-prose text-body-sm text-ink-inverse">
              {business.tagline}
            </p>

            <a
              href={business.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className={`${LINK_CLASS} mt-3`}
            >
              <FaFacebookF aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span>Facebook</span>
              <span className="sr-only">(opens in a new tab)</span>
            </a>
          </div>

          {/* Column 2 - services */}
          <nav className="min-w-0" aria-labelledby="footer-services-heading">
            <ColumnHeading id="footer-services-heading">
              Services
            </ColumnHeading>
            <ul className="mt-3">
              {footerServices.map((service) => (
                <li key={service.slug}>
                  <a href="#services" className={LINK_CLASS}>
                    {service.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Column 3 - company */}
          <nav className="min-w-0" aria-labelledby="footer-company-heading">
            <ColumnHeading id="footer-company-heading">
              Company
            </ColumnHeading>
            <ul className="mt-3">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className={LINK_CLASS}>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Column 4 - contact */}
          <div className="min-w-0">
            <ColumnHeading>Contact</ColumnHeading>
            <ul className="mt-3">
              <li>
                <a href={business.phone.href} className={LINK_CLASS}>
                  <LuPhone aria-hidden="true" className="h-4 w-4 shrink-0" />
                  <span className="tabular font-semibold">
                    {business.phone.display}
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={business.email.href}
                  className={`${LINK_CLASS} [overflow-wrap:anywhere]`}
                >
                  <LuMail aria-hidden="true" className="h-4 w-4 shrink-0" />
                  <span>{business.email.address}</span>
                </a>
              </li>

              {/*
                SLOT: address. `business.address` is null in src/content/site.js
                until the owner supplies it. It renders here automatically once
                the object is filled in - nothing is invented in the meantime.
              */}
              {business.address && (
                <li className="flex min-h-tap items-start gap-2 py-1 text-body text-ink-inverse">
                  <LuMapPin
                    aria-hidden="true"
                    className="mt-1 h-4 w-4 shrink-0"
                  />
                  <span>
                    {business.address.street}
                    <br />
                    {business.address.city}, {business.address.state}{" "}
                    {business.address.zip}
                  </span>
                </li>
              )}

              {/*
                SLOT: hours. Same rule as the address - `business.hours` is null
                until confirmed, so no schedule is shown until it is real.
              */}
              {business.hours && (
                <li className="flex min-h-tap items-start gap-2 py-1 text-body text-ink-inverse">
                  <LuClock
                    aria-hidden="true"
                    className="mt-1 h-4 w-4 shrink-0"
                  />
                  <span>
                    {business.hours.weekday}
                    {business.hours.saturday && (
                      <>
                        <br />
                        Saturday: {business.hours.saturday}
                      </>
                    )}
                    {business.hours.sunday && (
                      <>
                        <br />
                        Sunday: {business.hours.sunday}
                      </>
                    )}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 border-t border-line pt-6">
          {/*
            License number. Plain text is the cheapest, highest-trust signal in
            this trade. `business.licenseNumber` is null until the owner supplies
            it, and this line appears automatically the moment it is set.
          */}
          {business.licenseNumber && (
            <p className="text-body-sm text-ink-inverse">
              Texas electrical contractor license {business.licenseNumber}
            </p>
          )}

          <div className="mt-3 flex flex-col gap-2 text-body-sm text-ink-inverse sm:flex-row sm:items-center sm:justify-between">
            <p>
              &copy; {currentYear} {business.legalName}. All rights reserved.
            </p>
            <p>Serving {serviceAreas.corridor}.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
